import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getClients, addInvoice, updateInvoice, getClientById, getLatestClientInvoice, getInvoiceByNumber, getLatestRevenueInvoiceNumber } from '../../firebase/database';
import { generateRevenuesInvoicePDF, openPDFInNewTab } from '../../services/invoicePdfService';
import { LogisticsInvoice, Client, CostItem, SaleItem } from '../../types';
import { roundMoney, mulMoney, addMoney, subMoney, roundTo } from '../../utils/number';
import toast, { Toaster } from 'react-hot-toast';
import SuccessDialog from '../SuccessDialog';
import LoadingOverlay from '../LoadingOverlay';

// Item description suggestions
const ITEM_DESCRIPTION_SUGGESTIONS = [
  // Air Freight
  'AIR FREIGHT',
  'ADVANCE INSURANCE SURCHARGES',
  'AIRWAY BILL FEE',
  'CGC CHARGES',
  'CUSTOM CLEARANCE',
  
  // Ocean Freight
  'OCEAN FREIGHT',
  'BL FEE',
  'LOLO CHARGES',
  'SEAL AND CRO CHARGES',
  'HLA LIVE TRACKING CHARGES',
  'INSURANCE CHARGES',
  'TELEX CHARGES',
  'LOCAL TRANSPORTATION',
  'TRUCK DETAIN',
  'WHARFAGE CHARGES',
  'PLUGIN CHARGES',
  
  // General Charges
  'EFILING TOKEN',
  'PAYMENT AGAINST PSW GD FEE',
  'CUSTOM DUTY',
  'SALES TAX',
  'ADDITIONAL CUSTOM DUTY',
  'ADDITIONAL SALES TAX',
  'INCOME TAX',
  'PRA CHARGES',
  'STORAGE CHARGES',
  'CIVIL AVIATION CHARGES',
  'DELIVERY ORDER CHARGES',
  'APPRAISEMENT CHARGES',
  'EXAMINATION CHARGES',
  'DELIVERY AND MISL. CHARGES',
  'TRANSPORTATION CHARGES',
  'INVOICE NOT FOUND CHARGES',
  'EXTRA CUSTOM CHARGES',
  'AGENCY CHARGES'
];

// Autocomplete component for item descriptions
interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  required,
  inputRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input value
  useEffect(() => {
    if (value.length > 0) {
      const filtered = ITEM_DESCRIPTION_SUGGESTIONS.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredSuggestions(ITEM_DESCRIPTION_SUGGESTIONS);
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [value]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setFilteredSuggestions(ITEM_DESCRIPTION_SUGGESTIONS);
    setIsOpen(true);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          handleSuggestionClick(filteredSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        required={required}
        autoComplete="off"
      />
      
      {isOpen && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                index === highlightedIndex
                  ? 'bg-indigo-50 text-indigo-900'
                  : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Refs for input fields
  const selectedClientRef = useRef<HTMLSelectElement>(null);
  const logisticsCompanyRef = useRef<HTMLSelectElement>(null);
  const consigneeRef = useRef<HTMLInputElement>(null);
  const shipperNameRef = useRef<HTMLInputElement>(null);
  const invoiceDateRef = useRef<HTMLInputElement>(null);
  const airportOfDepartureRef = useRef<HTMLInputElement>(null);
  const airportOfDestinationRef = useRef<HTMLInputElement>(null);
  const awbNumberRef = useRef<HTMLInputElement>(null);
  const flightNoRef = useRef<HTMLInputElement>(null);
  const airlineNameRef = useRef<HTMLInputElement>(null);
  const gwWeightRef = useRef<HTMLInputElement>(null);
  const packageCountRefs = useRef<(HTMLInputElement | null)[]>([]);
  const portOfLoadingRef = useRef<HTMLInputElement>(null);
  const portOfDischargeRef = useRef<HTMLInputElement>(null);
  const blNumberRef = useRef<HTMLInputElement>(null);
  const shippingLineRef = useRef<HTMLInputElement>(null);
  const vesselVoyageRef = useRef<HTMLInputElement>(null);
  const containerQuantityRef = useRef<HTMLInputElement>(null);
  const containerNumberRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemRateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const costItemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const costItemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const costItemRateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saleItemRateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const discountRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLSelectElement>(null);
  const exchangeRateRef = useRef<HTMLInputElement>(null);
  
  // Invoice type selection
  const [invoiceType, setInvoiceType] = useState<'import' | 'export' | ''>('');
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  
  // Client selection
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [clientOutstanding, setClientOutstanding] = useState<number>(0);
  
  // Invoice dates
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // Logistics company details
  const [logisticsCompany, setLogisticsCompany] = useState<'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.' | ''>('');
  
  // Shipment details
  const [shippingMethod, setShippingMethod] = useState<'air' | 'sea'>('air');
  const [shipperName, setShipperName] = useState<string>('');
  const [consignee, setConsignee] = useState<string>('');
  
  // Air shipping fields
  const [airportOfDeparture, setAirportOfDeparture] = useState<string>('');
  const [airportOfDestination, setAirportOfDestination] = useState<string>('');
  const [awbNumber, setAwbNumber] = useState<string>('');
  const [flightNo, setFlightNo] = useState<string>('');
  const [airlineName, setAirlineName] = useState<string>('');
  const [gwWeight, setGwWeight] = useState<string>('');
  const [packageCount, setPackageCount] = useState<string[]>(['']);
  
  // Sea shipping fields
  const [portOfLoading, setPortOfLoading] = useState<string>('');
  const [portOfDischarge, setPortOfDischarge] = useState<string>('');
  const [blNumber, setBlNumber] = useState<string>('');
  const [shippingLine, setShippingLine] = useState<string>('');
  const [vesselVoyage, setVesselVoyage] = useState<string>('');
  const [containerQuantity, setContainerQuantity] = useState<string>('');
  const [containerNumber, setContainerNumber] = useState<string[]>(['']);
  
  // Financial details
  const [costItems, setCostItems] = useState<CostItem[]>([
    {
      id: '1',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: 'PKR'
    }
  ]);
  const [saleItems, setSaleItems] = useState<SaleItem[]>([
    {
      id: '1',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: 'PKR'
    }
  ]);
  const [activeTab, setActiveTab] = useState<'cost' | 'sale'>('cost');
  const [discount, setDiscount] = useState<number>(0);
  const [discountNature, setDiscountNature] = useState<string>('');
  const [currency, setCurrency] = useState<string>('PKR');
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [invoiceDescription, setInvoiceDescription] = useState<string>('');

  // Logistics company static data
  const logisticsCompanies = {
    'VECTOR LOGISTICS': {
      phone: 'Tel 042-35944728, 3588129',
      address: 'Office M-7, M Floor, Khan Arcade, Firdous Market, Gulberg-III, Lahore',
      logo: '/assets/vector_logo.png'
    },
    'BARQ CARGO SYSTEM PVT LTD.': {
      phone: 'Tel 042-35944728, 3588129',
      address: 'Office M-7, M Floor, Khan Arcade, Firdous Market, Gulberg-III, Lahore',
      logo: '/assets/barq_logo-min.png'
    }
  };
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...');
  
  // Draft invoice tracking - to update same draft instead of creating new ones
  const [draftInvoiceNumber, setDraftInvoiceNumber] = useState<string | null>(null);

  // Load clients on component mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientsData = await getClients();
        setClients(clientsData);
      } catch (error) {
        console.error('Error loading clients:', error);
      }
    };
    loadClients();
  }, []);

  // Filter clients based on invoice type
  useEffect(() => {
    if (invoiceType && clients.length > 0) {
      const filtered = clients.filter(client => 
        client.clientType === invoiceType || client.clientType === 'both'
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [invoiceType, clients]);

  // Update client outstanding when client changes
  useEffect(() => {
    if (selectedClient) {
      const client = filteredClients.find(c => c.id === selectedClient);
      setClientOutstanding(client?.outstanding || 0);
    }
  }, [selectedClient, filteredClients]);

  // Auto-populate fields with latest invoice data when client is selected
  useEffect(() => {
    const populateFromLatestInvoice = async () => {
      if (selectedClient) {
        try {
          const latestInvoice = await getLatestClientInvoice(selectedClient);
          if (latestInvoice) {
            // Populate logistics company
            if (latestInvoice.logisticsCompany) {
              setLogisticsCompany(latestInvoice.logisticsCompany);
            }
            
            // Populate shipper and consignee
            if (latestInvoice.shipperName) {
              setShipperName(latestInvoice.shipperName);
            }
            if (latestInvoice.consignee) {
              setConsignee(latestInvoice.consignee);
            }
            
            // Populate shipping method specific fields
            if (latestInvoice.shippingMethod) {
              setShippingMethod(latestInvoice.shippingMethod);
              
              if (latestInvoice.shippingMethod === 'air') {
                if (latestInvoice.airportOfDeparture) setAirportOfDeparture(latestInvoice.airportOfDeparture);
                if (latestInvoice.airportOfDestination) setAirportOfDestination(latestInvoice.airportOfDestination);
                if (latestInvoice.awbNumber) setAwbNumber(latestInvoice.awbNumber);
                if (latestInvoice.flightNo) setFlightNo(latestInvoice.flightNo);
                if (latestInvoice.airlineName) setAirlineName(latestInvoice.airlineName);
                if (latestInvoice.gwWeight) setGwWeight(latestInvoice.gwWeight);
                if (latestInvoice.packageCount) setPackageCount(Array.isArray(latestInvoice.packageCount) ? latestInvoice.packageCount : [latestInvoice.packageCount]);
              } else if (latestInvoice.shippingMethod === 'sea') {
                if (latestInvoice.portOfLoading) setPortOfLoading(latestInvoice.portOfLoading);
                if (latestInvoice.portOfDischarge) setPortOfDischarge(latestInvoice.portOfDischarge);
                if (latestInvoice.blNumber) setBlNumber(latestInvoice.blNumber);
                if (latestInvoice.shippingLine) setShippingLine(latestInvoice.shippingLine);
                if (latestInvoice.vesselVoyage) setVesselVoyage(latestInvoice.vesselVoyage);
                if (latestInvoice.containerQuantity) setContainerQuantity(latestInvoice.containerQuantity);
                if (latestInvoice.containerNumber) setContainerNumber(Array.isArray(latestInvoice.containerNumber) ? latestInvoice.containerNumber : [latestInvoice.containerNumber]);
              }
            }
            
            // Populate financial details
            if (latestInvoice.currency) setCurrency(latestInvoice.currency);
            if (typeof latestInvoice.exchangeRate === 'number') setExchangeRate(latestInvoice.exchangeRate);
            if (latestInvoice.invoiceDescription) setInvoiceDescription(latestInvoice.invoiceDescription);
            
            // Populate cost items
            if (latestInvoice.costItems && latestInvoice.costItems.length > 0) {
              setCostItems(latestInvoice.costItems.map((item, index) => ({
                id: (index + 1).toString(),
                description: item.description || '',
                quantity: item.quantity || 1,
                rate: item.rate || 0,
                amount: item.amount || 0,
                currency: item.currency || 'PKR'
              })));
            }
            
            // Populate sale items
            if (latestInvoice.saleItems && latestInvoice.saleItems.length > 0) {
              setSaleItems(latestInvoice.saleItems.map((item, index) => ({
                id: (index + 1).toString(),
                description: item.description || '',
                quantity: item.quantity || 1,
                rate: item.rate || 0,
                amount: item.amount || 0,
                currency: item.currency || 'PKR'
              })));
            }
            
            toast.success('Fields populated from last invoice', { position: 'bottom-right' });
          }
        } catch (error) {
          console.error('Error fetching latest invoice:', error);
        }
      }
    };
    
    populateFromLatestInvoice();
  }, [selectedClient]);

  // Handle invoice type selection
  const handleInvoiceTypeSelect = (type: 'import' | 'export') => {
    setInvoiceType(type);
    setShowInvoiceForm(true);
    // Reset form when changing invoice type
    setSelectedClient('');
    setLogisticsCompany('');
    setConsignee('');
    
    // Reset air shipping fields
    setAirportOfDeparture('');
    setAirportOfDestination('');
    setAwbNumber('');
    setFlightNo('');
    setAirlineName('');
    setGwWeight('');
    setPackageCount(['']);
    
    // Reset sea shipping fields
    setPortOfLoading('');
    setPortOfDischarge('');
    setBlNumber('');
    setShippingLine('');
    setVesselVoyage('');
    setContainerQuantity('');
    setContainerNumber(['']);
    
    // Reset financial fields
    setCostItems([{
      id: '1',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: 'PKR'
    }]);
    setSaleItems([{
      id: '1',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: 'PKR'
    }]);
    setActiveTab('cost');
    setDiscount(0);
    setDiscountNature('');
    setCurrency('PKR');
    setExchangeRate(1);
    setInvoiceDescription('');
  };

  const formatSequenceNumber = (sequence: number): string => {
    return sequence.toString().padStart(5, '0');
  };

  const extractSequenceFromNumber = (invoiceNumber: string, year: number): number | null => {
    // Try format: YYYY-MM-NNNNN or YYYY-MM-NNNN
    let match = invoiceNumber.match(/^(\d{4})-(\d{2})-(\d+)$/);
    if (match) {
      const invoiceYear = parseInt(match[1], 10);
      const sequence = parseInt(match[3], 10);
      // Only check year, sequence continues across months
      if (invoiceYear === year) {
        return sequence;
      }
      return null;
    }
    
    // Try old format without month: YYYY-NNNNN
    match = invoiceNumber.match(/^(\d{4})-(\d+)$/);
    if (match) {
      const invoiceYear = parseInt(match[1], 10);
      const sequence = parseInt(match[2], 10);
      if (invoiceYear === year) {
        return sequence;
      }
      return null;
    }
    
    return null;
  };

  // Generate invoice number with sequential unique IDs
  const generateInvoiceNumber = async (): Promise<string> => {
    const date = invoiceDate ? new Date(invoiceDate) : new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const prefix = `${year}-${month}-`;

    try {
      const latestInvoiceNumber = await getLatestRevenueInvoiceNumber();
      let sequence = 1;

      if (latestInvoiceNumber) {
        // Extract sequence based on year only (continues across months)
        const latestSequence = extractSequenceFromNumber(latestInvoiceNumber, year);
        if (latestSequence !== null) {
          sequence = latestSequence + 1;
        }
      }

      const maxAttempts = 100;
      let attempts = 0;

      while (attempts < maxAttempts) {
        const candidate = `${prefix}${formatSequenceNumber(sequence)}`;
        const existingInvoice = await getInvoiceByNumber(candidate);
        if (!existingInvoice) {
          return candidate;
        }
        sequence += 1;
        attempts += 1;
      }

      throw new Error('Unable to generate unique invoice number');
    } catch (error) {
      console.warn('Error generating invoice number:', error);
      const fallback = Date.now().toString().slice(-5).padStart(5, '0');
      return `${prefix}${fallback}`;
    }
  };


  // Add new cost item
  const addCostItem = () => {
    const newItem: CostItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: currency
    };
    setCostItems([...costItems, newItem]);
  };

  // Remove cost item
  const removeCostItem = (id: string) => {
    if (costItems.length > 1) {
      setCostItems(costItems.filter(item => item.id !== id));
    }
  };

  // Update cost item
  const updateCostItem = (id: string, field: keyof CostItem, value: string | number) => {
    setCostItems(costItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate amount for quantity, rate, or currency changes
        if (field === 'quantity' || field === 'rate' || field === 'currency') {
          updatedItem.amount = mulMoney(updatedItem.quantity, updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  // Add new sale item
  const addSaleItem = () => {
    const newItem: SaleItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: currency
    };
    setSaleItems([...saleItems, newItem]);
  };

  // Remove sale item
  const removeSaleItem = (id: string) => {
    if (saleItems.length > 1) {
      setSaleItems(saleItems.filter(item => item.id !== id));
    }
  };

  // Update sale item
  const updateSaleItem = (id: string, field: keyof SaleItem, value: string | number) => {
    setSaleItems(saleItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate amount for quantity, rate, or currency changes
        if (field === 'quantity' || field === 'rate' || field === 'currency') {
          updatedItem.amount = mulMoney(updatedItem.quantity, updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  // Calculate totals (legacy - using sale items now)
  const calculateSubtotal = () => {
    return roundMoney(saleItems.reduce((sum, item) => addMoney(sum, item.amount), 0));
  };

  const calculateDiscount = () => {
    return roundMoney(discount);
  };

  const calculateTotal = () => {
    return subMoney(calculateSubtotal(), calculateDiscount());
  };

  // Calculate PKR totals (using sale items)
  const calculateSubtotalPKR = () => {
    const total = saleItems.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0);
    return roundMoney(total);
  };

  const calculateDiscountPKR = () => {
    return roundMoney(discount);
  };

  const calculateTotalPKR = () => {
    const subtotal = calculateSubtotalPKR();
    const discountAmount = roundMoney(discount);
    const raw = subMoney(subtotal, discountAmount);
    return roundMoney(raw);
  };

  // Calculate cost totals (using cost items)
  const calculateCostSubtotalPKR = () => {
    const total = costItems.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0);
    return roundMoney(total);
  };

  // Calculate Gross Profit: Sales - Cost
  const calculateGrossProfit = () => {
    const saleTotal = calculateSubtotalPKR();
    const costTotal = calculateCostSubtotalPKR();
    return subMoney(saleTotal, costTotal);
  };

  // Calculate Net Profit: Gross Profit - Deduction
  const calculateNetProfit = () => {
    const grossProfit = calculateGrossProfit();
    const deductionAmount = roundMoney(discount);
    return subMoney(grossProfit, deductionAmount);
  };

  const generateRandomPrice = () => {
    // Limit auto-populated item rate to 1 or 2 (currency-aware elsewhere)
    return Math.random() < 0.5 ? 1 : 2;
  };

  const populateByAir = () => {
    setSelectedClient('client1'); // Assuming you have a client with this ID
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setLogisticsCompany('VECTOR LOGISTICS');
    setShippingMethod('air');
    setShipperName('ABC Trading Co.');
    setConsignee('XYZ Importers Ltd.');
    setAirportOfDeparture('JFK International Airport');
    setAirportOfDestination('Jinnah International Airport');
    setAwbNumber(`AWB${Date.now()}`);
    setFlightNo('PK-789');
    setAirlineName('Pakistan International Airlines');
    setGwWeight('45.5 kg');
    setPackageCount(['3 packages', '1 pallet']);
    
    const item1Rate = generateRandomPrice();
    const item2Rate = generateRandomPrice();
    
    // Set cost items
    setCostItems([
      {
        id: '1',
        description: 'Electronics Components',
        quantity: 2,
        rate: item1Rate * 0.8, // Cost is 80% of sale price
        amount: 2 * item1Rate * 0.8,
        currency: 'USD'
      },
      {
        id: '2',
        description: 'Customs Clearance Services',
        quantity: 1,
        rate: item2Rate * 0.7, // Cost is 70% of sale price
        amount: 1 * item2Rate * 0.7,
        currency: 'PKR'
      }
    ]);

    // Set sale items
    setSaleItems([
      {
        id: '1',
        description: 'Electronics Components',
        quantity: 2,
        rate: item1Rate,
        amount: 2 * item1Rate,
        currency: 'USD'
      },
      {
        id: '2',
        description: 'Customs Clearance Services',
        quantity: 1,
        rate: item2Rate,
        amount: 1 * item2Rate,
        currency: 'PKR'
      }
    ]);

    setDiscount(5);
    setDiscountNature('Early payment discount');
    setCurrency('USD');
    setExchangeRate(280);
    setInvoiceDescription('Electronics Components Import Services');
  };

  const populateBySea = () => {
    setSelectedClient('client2'); // Assuming you have a client with this ID
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setLogisticsCompany('BARQ CARGO SYSTEM PVT LTD.');
    setShippingMethod('sea');
    setShipperName('DEF Exporters Ltd.');
    setConsignee('GHI Trading Company');
    setPortOfLoading('Port of Karachi');
    setPortOfDischarge('Port of Dubai');
    setBlNumber(`BL${Date.now()}`);
    setShippingLine('Maersk Line');
    setVesselVoyage('MV Pacific Voyager / V001');
    setContainerQuantity('2 containers');
    setContainerNumber(['ABCD1234567', 'EFGH7654321']);
    
    const item1Rate = generateRandomPrice();
    const item2Rate = generateRandomPrice();
    const item3Rate = generateRandomPrice();
    
    // Set cost items
    setCostItems([
      {
        id: '1',
        description: 'Textile Products',
        quantity: 5,
        rate: item1Rate * 0.75, // Cost is 75% of sale price
        amount: 5 * item1Rate * 0.75,
        currency: 'PKR'
      },
      {
        id: '2',
        description: 'Freight Charges',
        quantity: 1,
        rate: item2Rate * 0.8, // Cost is 80% of sale price
        amount: 1 * item2Rate * 0.8,
        currency: 'USD'
      },
      {
        id: '3',
        description: 'Documentation Services',
        quantity: 1,
        rate: item3Rate * 0.6, // Cost is 60% of sale price
        amount: 1 * item3Rate * 0.6,
        currency: 'PKR'
      }
    ]);

    // Set sale items
    setSaleItems([
      {
        id: '1',
        description: 'Textile Products',
        quantity: 5,
        rate: item1Rate,
        amount: 5 * item1Rate,
        currency: 'PKR'
      },
      {
        id: '2',
        description: 'Freight Charges',
        quantity: 1,
        rate: item2Rate,
        amount: 1 * item2Rate,
        currency: 'USD'
      },
      {
        id: '3',
        description: 'Documentation Services',
        quantity: 1,
        rate: item3Rate,
        amount: 1 * item3Rate,
        currency: 'PKR'
      }
    ]);

    setDiscount(3);
    setDiscountNature('Bulk shipment discount');
    setCurrency('PKR');
    setExchangeRate(1);
    setInvoiceDescription('Textile Products Export Services');
  };

  // Preview PDF
  const handlePreviewPDF = async () => {
    setShowValidation(true);
    // Small delay to ensure all state updates are complete
    await new Promise(resolve => setTimeout(resolve, 50));
    if (!validateForm()) {
      return;
    }
    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF preview...');
    try {
      const invoiceNumber = await generateInvoiceNumber();
      const invoice: LogisticsInvoice = {
        id: 'preview',
        invoiceNumber: invoiceNumber,
        clientId: selectedClient,
        clientName: filteredClients.find(c => c.id === selectedClient)?.name || '',
        invoiceType: invoiceType as 'import' | 'export',
        date: new Date(invoiceDate),
        logisticsCompany: logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
        shippingMethod,
        shipperName,
        consignee,
        airportOfDeparture: shippingMethod === 'air' ? airportOfDeparture : undefined,
        airportOfDestination: shippingMethod === 'air' ? airportOfDestination : undefined,
        awbNumber: shippingMethod === 'air' ? awbNumber : undefined,
        flightNo: shippingMethod === 'air' ? flightNo : undefined,
        airlineName: shippingMethod === 'air' ? airlineName : undefined,
        gwWeight: shippingMethod === 'air' ? gwWeight : undefined,
        packageCount: shippingMethod === 'air' ? packageCount.filter(v => v && v.trim() !== '') : undefined,
        portOfLoading: shippingMethod === 'sea' ? portOfLoading : undefined,
        portOfDischarge: shippingMethod === 'sea' ? portOfDischarge : undefined,
        blNumber: shippingMethod === 'sea' ? blNumber : undefined,
        shippingLine: shippingMethod === 'sea' ? shippingLine : undefined,
        vesselVoyage: shippingMethod === 'sea' ? vesselVoyage : undefined,
        containerQuantity: shippingMethod === 'sea' ? containerQuantity : undefined,
        containerNumber: shippingMethod === 'sea' ? containerNumber.filter(v => v && v.trim() !== '') : undefined,
        costItems,
        saleItems,
        subtotal: calculateSubtotalPKR(),
        discount: calculateDiscountPKR(),
        discountNature,
        total: calculateTotalPKR(),
        currency,
        exchangeRate,
        invoiceDescription,
        status: 'pending',
        createdBy: currentUser?.id || '',
        createdAt: new Date()
      };

      // Get client address
      const client = await getClientById(invoice.clientId);
      const clientAddress = client?.address;
      
      const pdf = await generateRevenuesInvoicePDF(invoice, clientAddress);
      openPDFInNewTab(pdf, 'Invoice PDF');
      toast.success('PDF preview generated successfully!', { position: 'bottom-right' });
    } catch (error) {
      toast.error('Error generating PDF preview', { position: 'bottom-right' });
      console.error('PDF generation error:', error);
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  // Scroll to first empty field
  const scrollToFirstEmptyField = (errors: Record<string, string>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Priority order for scrolling to fields
    const fieldPriority = [
      'selectedClient',
      'logisticsCompany', 
      'consignee',
      'shipperName',
      'invoiceDate',
      'airportOfDeparture',
      'airportOfDestination',
      'awbNumber',
      'flightNo',
      'airlineName',
      'gwWeight',
      'packageCount',
      'portOfLoading',
      'portOfDischarge',
      'blNumber',
      'shippingLine',
      'vesselVoyage',
      'containerQuantity',
      'containerNumber',
      'currency',
      'exchangeRate'
    ];

    // Find the first error field in priority order
    let targetField = null;
    for (const priorityField of fieldPriority) {
      if (errors[priorityField]) {
        targetField = priorityField;
        break;
      }
    }

    // If no priority field found, check for item errors
    if (!targetField) {
      for (const errorKey of errorKeys) {
        if (errorKey.startsWith('item_')) {
          targetField = errorKey;
          break;
        }
      }
    }

    // Scroll to the target field
    if (targetField) {
      let targetRef: React.RefObject<HTMLElement> | null = null;
      
      switch (targetField) {
        case 'selectedClient':
          targetRef = selectedClientRef;
          break;
        case 'logisticsCompany':
          targetRef = logisticsCompanyRef;
          break;
        case 'consignee':
          targetRef = consigneeRef;
          break;
        case 'shipperName':
          targetRef = shipperNameRef;
          break;
        case 'invoiceDate':
          targetRef = invoiceDateRef;
          break;
        case 'airportOfDeparture':
          targetRef = airportOfDepartureRef;
          break;
        case 'airportOfDestination':
          targetRef = airportOfDestinationRef;
          break;
        case 'awbNumber':
          targetRef = awbNumberRef;
          break;
        case 'flightNo':
          targetRef = flightNoRef;
          break;
        case 'airlineName':
          targetRef = airlineNameRef;
          break;
        case 'gwWeight':
          targetRef = gwWeightRef;
          break;
        case 'packageCount':
          targetRef = { current: packageCountRefs.current.find((r) => !!r) as any } as any;
          break;
        case 'portOfLoading':
          targetRef = portOfLoadingRef;
          break;
        case 'portOfDischarge':
          targetRef = portOfDischargeRef;
          break;
        case 'blNumber':
          targetRef = blNumberRef;
          break;
        case 'shippingLine':
          targetRef = shippingLineRef;
          break;
        case 'vesselVoyage':
          targetRef = vesselVoyageRef;
          break;
        case 'containerQuantity':
          targetRef = containerQuantityRef;
          break;
        case 'containerNumber':
          targetRef = { current: containerNumberRefs.current.find((r) => !!r) as any } as any;
          break;
        case 'currency':
          targetRef = currencyRef;
          break;
        case 'exchangeRate':
          targetRef = exchangeRateRef;
          break;
        default:
          // Handle item errors
          if (targetField.startsWith('item_')) {
            const parts = targetField.split('_');
            const index = parseInt(parts[1]);
            const field = parts[2];
            
            if (field === 'description' && itemDescriptionRefs.current[index]) {
              targetRef = { current: itemDescriptionRefs.current[index] };
            } else if (field === 'quantity' && itemQuantityRefs.current[index]) {
              targetRef = { current: itemQuantityRefs.current[index] };
            } else if (field === 'rate' && itemRateRefs.current[index]) {
              targetRef = { current: itemRateRefs.current[index] };
            }
          }
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        targetRef.current.focus();
      }
    }
  };

  // Validate form
  const validateDraftForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Both client and company selection are required for draft
    if (!selectedClient) {
      errors['selectedClient'] = 'Required';
    }
    
    if (!logisticsCompany) {
      errors['logisticsCompany'] = 'Required';
    }
    
    if (!selectedClient || !logisticsCompany) {
      toast.error('Both Party and Company selection are required to save draft', { position: 'bottom-right' });
    }
    
    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Check required fields and show toast for each missing field
    if (!selectedClient) {
      errors['selectedClient'] = 'Required';
      toast.error('Party selection is required', { position: 'bottom-right' });
    }
    if (!logisticsCompany) {
      errors['logisticsCompany'] = 'Required';
      toast.error('Logistics company selection is required', { position: 'bottom-right' });
    }
    if (!consignee) {
      errors['consignee'] = 'Required';
      toast.error('Consignee is required', { position: 'bottom-right' });
    }
    if (!shipperName) {
      errors['shipperName'] = 'Required';
      toast.error('Shipper name is required', { position: 'bottom-right' });
    }
    if (!invoiceDate) {
      errors['invoiceDate'] = 'Required';
      toast.error('Invoice date is required', { position: 'bottom-right' });
    }
    if (discount < 0) {
      errors['discount'] = 'Deduction must be 0 or more';
      toast.error('Deduction must be 0 or more', { position: 'bottom-right' });
    }
    if (!currency) {
      errors['currency'] = 'Required';
      toast.error('Currency selection is required', { position: 'bottom-right' });
    }
    if (exchangeRate < 0) {
      errors['exchangeRate'] = 'Exchange rate cannot be negative';
      toast.error('Exchange rate cannot be negative', { position: 'bottom-right' });
    }
    if (!invoiceDescription.trim()) {
      errors['invoiceDescription'] = 'Required';
      toast.error('Invoice description is required', { position: 'bottom-right' });
    }
    
    if (shippingMethod === 'air') {
      if (!airportOfDeparture) {
        errors['airportOfDeparture'] = 'Required';
        toast.error('Airport of departure is required', { position: 'bottom-right' });
      }
      if (!airportOfDestination) {
        errors['airportOfDestination'] = 'Required';
        toast.error('Airport of destination is required', { position: 'bottom-right' });
      }
      if (!awbNumber) {
        errors['awbNumber'] = 'Required';
        toast.error('AWB number is required', { position: 'bottom-right' });
      }
      if (!flightNo) {
        errors['flightNo'] = 'Required';
        toast.error('Flight number is required', { position: 'bottom-right' });
      }
      if (!airlineName) {
        errors['airlineName'] = 'Required';
        toast.error('Airline name is required', { position: 'bottom-right' });
      }
      if (!gwWeight) {
        errors['gwWeight'] = 'Required';
        toast.error('Gross weight is required', { position: 'bottom-right' });
      }
      const hasPackage = Array.isArray(packageCount) ? packageCount.some(v => v && v.trim() !== '') : !!packageCount;
      if (!hasPackage) {
        errors['packageCount'] = 'Required';
        toast.error('At least one package count is required', { position: 'bottom-right' });
      }
    } else if (shippingMethod === 'sea') {
      if (!portOfLoading) {
        errors['portOfLoading'] = 'Required';
        toast.error('Port of loading is required', { position: 'bottom-right' });
      }
      if (!portOfDischarge) {
        errors['portOfDischarge'] = 'Required';
        toast.error('Port of discharge is required', { position: 'bottom-right' });
      }
      if (!blNumber) {
        errors['blNumber'] = 'Required';
        toast.error('BL number is required', { position: 'bottom-right' });
      }
      if (!shippingLine) {
        errors['shippingLine'] = 'Required';
        toast.error('Shipping line is required', { position: 'bottom-right' });
      }
      if (!vesselVoyage) {
        errors['vesselVoyage'] = 'Required';
        toast.error('Vessel/Voyage is required', { position: 'bottom-right' });
      }
      if (!containerQuantity) {
        errors['containerQuantity'] = 'Required';
        toast.error('Container quantity is required', { position: 'bottom-right' });
      }
      const hasContainer = Array.isArray(containerNumber) ? containerNumber.some(v => v && v.trim() !== '') : !!containerNumber;
      if (!hasContainer) {
        errors['containerNumber'] = 'Required';
        toast.error('At least one container number is required', { position: 'bottom-right' });
      }
    }
    
    // Validate cost items
    costItems.forEach((item, index) => {
      if (!item.description || item.description.trim() === '') {
        errors[`cost_item_${index}_description`] = 'Required';
        toast.error(`Cost Item ${index + 1} description is required`, { position: 'bottom-right' });
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`cost_item_${index}_quantity`] = 'Must be > 0';
        toast.error(`Cost Item ${index + 1} quantity must be greater than 0`, { position: 'bottom-right' });
      }
      if (!item.rate || item.rate <= 0) {
        errors[`cost_item_${index}_rate`] = 'Must be > 0';
        toast.error(`Cost Item ${index + 1} rate must be greater than 0`, { position: 'bottom-right' });
      }
    });

    // Validate sale items
    saleItems.forEach((item, index) => {
      if (!item.description || item.description.trim() === '') {
        errors[`sale_item_${index}_description`] = 'Required';
        toast.error(`Sale Item ${index + 1} description is required`, { position: 'bottom-right' });
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`sale_item_${index}_quantity`] = 'Must be > 0';
        toast.error(`Sale Item ${index + 1} quantity must be greater than 0`, { position: 'bottom-right' });
      }
      if (!item.rate || item.rate <= 0) {
        errors[`sale_item_${index}_rate`] = 'Must be > 0';
        toast.error(`Sale Item ${index + 1} rate must be greater than 0`, { position: 'bottom-right' });
      }
    });

    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Submit invoice
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    // Small delay to ensure all state updates are complete
    await new Promise(resolve => setTimeout(resolve, 50));
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setIsLoading(true);
    setLoadingMessage('Creating invoice...');
    try {
      // Check if there's an existing draft to update
      const finalInvoiceNumber = draftInvoiceNumber || await generateInvoiceNumber();
      
      const invoice: Omit<LogisticsInvoice, 'id' | 'createdAt'> = {
        invoiceNumber: finalInvoiceNumber,
        clientId: selectedClient,
        clientName: filteredClients.find(c => c.id === selectedClient)?.name || '',
        invoiceType: invoiceType as 'import' | 'export',
        date: new Date(invoiceDate),
        logisticsCompany: logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
        shippingMethod,
        shipperName,
        consignee,
        // Air shipping fields - only include if shipping method is air
        airportOfDeparture: shippingMethod === 'air' ? airportOfDeparture : undefined,
        airportOfDestination: shippingMethod === 'air' ? airportOfDestination : undefined,
        awbNumber: shippingMethod === 'air' ? awbNumber : undefined,
        flightNo: shippingMethod === 'air' ? flightNo : undefined,
        airlineName: shippingMethod === 'air' ? airlineName : undefined,
        gwWeight: shippingMethod === 'air' ? gwWeight : undefined,
        packageCount: shippingMethod === 'air' ? packageCount : undefined,
        // Sea shipping fields - only include if shipping method is sea
        portOfLoading: shippingMethod === 'sea' ? portOfLoading : undefined,
        portOfDischarge: shippingMethod === 'sea' ? portOfDischarge : undefined,
        blNumber: shippingMethod === 'sea' ? blNumber : undefined,
        shippingLine: shippingMethod === 'sea' ? shippingLine : undefined,
        vesselVoyage: shippingMethod === 'sea' ? vesselVoyage : undefined,
        containerQuantity: shippingMethod === 'sea' ? containerQuantity : undefined,
        containerNumber: shippingMethod === 'sea' ? containerNumber : undefined,
        costItems,
        saleItems,
        subtotal: calculateSubtotalPKR(),
        discount: calculateDiscountPKR(),
        discountNature,
        total: calculateTotalPKR(),
        currency,
        exchangeRate,
        invoiceDescription,
        status: 'pending',
        createdBy: currentUser?.id || ''
      };

      // Remove undefined fields before sending to Firebase
      const cleanInvoice = Object.fromEntries(
        Object.entries(invoice).filter(([_, value]) => value !== undefined)
      );

      // If there's an existing draft, update it; otherwise create a new invoice
      if (draftInvoiceNumber) {
        await updateInvoice(finalInvoiceNumber, cleanInvoice as Partial<Omit<LogisticsInvoice, 'id' | 'createdAt' | 'approvedAt'>>);
        toast.success('Draft converted to invoice successfully!', { position: 'bottom-right' });
      } else {
        await addInvoice(cleanInvoice as Omit<LogisticsInvoice, 'id' | 'createdAt'>);
        toast.success('Invoice created successfully!', { position: 'bottom-right' });
      }
      setShowSuccessDialog(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(errorMessage, { position: 'bottom-right' });
      console.error('Invoice creation error:', error);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Save as draft
  const handleSaveDraft = async () => {
    setShowValidation(true);
    if (!validateDraftForm()) {
      return;
    }
    setIsSavingDraft(true);
    setIsLoading(true);
    setLoadingMessage('Saving draft...');
    try {
      // Reuse existing draft invoice number or generate new one
      let invoiceNumber = draftInvoiceNumber;
      if (!invoiceNumber) {
        invoiceNumber = await generateInvoiceNumber();
        setDraftInvoiceNumber(invoiceNumber); // Store for subsequent saves
      }
      
      const invoice: Omit<LogisticsInvoice, 'id' | 'createdAt'> = {
        invoiceNumber: invoiceNumber,
        clientId: selectedClient,
        clientName: filteredClients.find(c => c.id === selectedClient)?.name || '',
        invoiceType: (invoiceType as 'import' | 'export') || 'import',
        date: new Date(invoiceDate || new Date().toISOString().split('T')[0]),
        logisticsCompany: (logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.') || 'VECTOR LOGISTICS',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
        shippingMethod: shippingMethod || 'air',
        shipperName: shipperName || '',
        consignee: consignee || '',
        // Air shipping fields - only include if shipping method is air
        airportOfDeparture: shippingMethod === 'air' ? airportOfDeparture : undefined,
        airportOfDestination: shippingMethod === 'air' ? airportOfDestination : undefined,
        awbNumber: shippingMethod === 'air' ? awbNumber : undefined,
        flightNo: shippingMethod === 'air' ? flightNo : undefined,
        airlineName: shippingMethod === 'air' ? airlineName : undefined,
        gwWeight: shippingMethod === 'air' ? gwWeight : undefined,
        packageCount: shippingMethod === 'air' ? packageCount : undefined,
        // Sea shipping fields - only include if shipping method is sea
        portOfLoading: shippingMethod === 'sea' ? portOfLoading : undefined,
        portOfDischarge: shippingMethod === 'sea' ? portOfDischarge : undefined,
        blNumber: shippingMethod === 'sea' ? blNumber : undefined,
        shippingLine: shippingMethod === 'sea' ? shippingLine : undefined,
        vesselVoyage: shippingMethod === 'sea' ? vesselVoyage : undefined,
        containerQuantity: shippingMethod === 'sea' ? containerQuantity : undefined,
        containerNumber: shippingMethod === 'sea' ? containerNumber : undefined,
        // Financial details
        costItems: costItems.length > 0 ? costItems : [], // Allow empty cost items for draft
        saleItems: saleItems.length > 0 ? saleItems : [], // Allow empty sale items for draft
        subtotal: calculateSubtotalPKR(),
        discount: discount || 0,
        discountNature: discountNature || '',
        total: calculateTotalPKR(),
        currency: currency || 'PKR',
        exchangeRate: typeof exchangeRate === 'number' ? exchangeRate : 0,
        invoiceDescription: invoiceDescription || '',
        status: 'draft',
        createdBy: currentUser?.id || '',
      };

      // Remove undefined fields before sending to Firebase
      const cleanInvoice = Object.fromEntries(
        Object.entries(invoice).filter(([_, value]) => value !== undefined)
      );

      // Check if this is an update to existing draft or a new draft
      if (draftInvoiceNumber) {
        // Update existing draft
        await updateInvoice(invoiceNumber, cleanInvoice as Partial<Omit<LogisticsInvoice, 'id' | 'createdAt' | 'approvedAt'>>);
        toast.success('Draft updated successfully!', { position: 'bottom-right' });
      } else {
        // Create new draft
        await addInvoice(cleanInvoice as Omit<LogisticsInvoice, 'id' | 'createdAt'>);
        toast.success('Draft saved successfully!', { position: 'bottom-right' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(errorMessage, { position: 'bottom-right' });
      console.error('Draft save error:', error);
    } finally {
      setIsSavingDraft(false);
      setIsLoading(false);
    }
  };

  // Show invoice type selection if not selected yet
  if (!showInvoiceForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
        <Toaster />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Create Logistics Invoice</h1>
                <p className="mt-2 text-gray-600">Select the invoice type to proceed</p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Invoice Type Selection */}
          <div className="bg-white shadow rounded-lg p-8">
            <h2 className="text-xl font-medium text-gray-900 mb-6 text-center">Select Invoice Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={() => handleInvoiceTypeSelect('import')}
                className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-center"
              >
                <div className="text-4xl mb-4">📥</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Import Invoice</h3>
                <p className="text-gray-600">Create an invoice for import logistics services</p>
              </button>
              <button
                onClick={() => handleInvoiceTypeSelect('export')}
                className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-500"
              >
                <div className="text-4xl mb-4">📤</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Export Invoice</h3>
                <p className="text-gray-600">Create an invoice for export logistics services</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster />
      <LoadingOverlay isVisible={isLoading} message={loadingMessage} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create {invoiceType === 'import' ? 'Import' : 'Export'} Invoice</h1>
              <p className="mt-2 text-gray-600">Create a new {invoiceType} logistics invoice</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowInvoiceForm(false);
                  setInvoiceType('');
                  setSelectedClient('');
                  setLogisticsCompany('');
                  setInvoiceDescription('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Change Type
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Keep top-level errors minimal; action errors appear near buttons */}

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Development Populate Buttons */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Development Tools</h3>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={populateByAir}
                className="px-4 py-2 text-sm font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200"
              >
                Auto-Populate by Air
              </button>
              <button
                type="button"
                onClick={populateBySea}
                className="px-4 py-2 text-sm font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200"
              >
                Auto-Populate by Sea
              </button>
            </div>
          </div>

          {/* Client Selection */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Party Name</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Party *
                </label>
                <select
                  ref={selectedClientRef}
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Choose a party</option>
                  {filteredClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} - {client.clientType} (Outstanding: PKR {client.outstanding.toLocaleString()})
                    </option>
                  ))}
                </select>
                {showValidation && fieldErrors['selectedClient'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['selectedClient']}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Showing {invoiceType} parties only
                </p>
              </div>
              {selectedClient && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600">
                    <strong>Outstanding Balance:</strong> PKR {clientOutstanding.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>


          {/* Logistics Company Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Logistics Company Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Company *
                </label>
                <select
                  ref={logisticsCompanyRef}
                  value={logisticsCompany}
                  onChange={(e) => setLogisticsCompany(e.target.value as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Choose a company</option>
                  <option value="VECTOR LOGISTICS">VECTOR LOGISTICS</option>
                  <option value="BARQ CARGO SYSTEM PVT LTD.">BARQ CARGO SYSTEM PVT LTD.</option>
                </select>
              </div>
              {showValidation && fieldErrors['logisticsCompany'] && (
                <p className="text-sm text-red-600">{fieldErrors['logisticsCompany']}</p>
              )}
              {logisticsCompany && (
                <div className="bg-gray-50 p-4 rounded-md flex items-center gap-4">
                  <div>
                    <p className="text-sm text-gray-600">
                      <strong>Phone:</strong> {logisticsCompanies[logisticsCompany].phone}<br />
                      <strong>Address:</strong> {logisticsCompanies[logisticsCompany].address}
                    </p>
                  </div>
                  <img src={logisticsCompanies[logisticsCompany].logo} alt={`${logisticsCompany} logo`} className="h-10 w-auto" />
                </div>
              )}
            </div>
          </div>

          {/* Shipment Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Shipment Details</h2>
            
            {/* Shipping Method Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipping Method *
              </label>
              <select
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value as 'air' | 'sea')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="air">Air</option>
                <option value="sea">Sea</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shipper Name *
                </label>
                <input
                  ref={shipperNameRef}
                  type="text"
                  value={shipperName}
                  onChange={(e) => setShipperName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter shipper name"
                  required
                />
                {showValidation && fieldErrors['shipperName'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['shipperName']}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">Enter the shipper name manually</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consignee *
                </label>
                <input
                  ref={consigneeRef}
                  type="text"
                  value={consignee}
                  onChange={(e) => setConsignee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {showValidation && fieldErrors['consignee'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['consignee']}</p>
                )}
              </div>
            </div>

            
            {/* Air Shipping Fields */}
            {shippingMethod === 'air' && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Airport of Departure *
                  </label>
                  <input
                    ref={airportOfDepartureRef}
                    type="text"
                    value={airportOfDeparture}
                    onChange={(e) => setAirportOfDeparture(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['airportOfDeparture'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['airportOfDeparture']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Airport of Destination *
                  </label>
                  <input
                    ref={airportOfDestinationRef}
                    type="text"
                    value={airportOfDestination}
                    onChange={(e) => setAirportOfDestination(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['airportOfDestination'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['airportOfDestination']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AWB Number *
                  </label>
                  <input
                    ref={awbNumberRef}
                    type="text"
                    value={awbNumber}
                    onChange={(e) => setAwbNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['awbNumber'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['awbNumber']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flight No *
                  </label>
                  <input
                    ref={flightNoRef}
                    type="text"
                    value={flightNo}
                    onChange={(e) => setFlightNo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['flightNo'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['flightNo']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Airline Name *
                  </label>
                  <input
                    ref={airlineNameRef}
                    type="text"
                    value={airlineName}
                    onChange={(e) => setAirlineName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['airlineName'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['airlineName']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gross Weight *
                  </label>
                  <input
                    ref={gwWeightRef}
                    type="text"
                    value={gwWeight}
                    onChange={(e) => setGwWeight(e.target.value)}
                    placeholder="e.g., 25.5 kg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['gwWeight'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['gwWeight']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Count *
                  </label>
                  <div className="space-y-2">
                    {packageCount.map((val, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          ref={(el) => (packageCountRefs.current[idx] = el)}
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const next = [...packageCount];
                            next[idx] = e.target.value;
                            setPackageCount(next);
                          }}
                          placeholder={idx === 0 ? 'e.g., 2 packages' : 'Additional package'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required={idx === 0}
                        />
                        <div className="flex items-center gap-1">
                          {packageCount.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setPackageCount(packageCount.filter((_, i) => i !== idx))}
                              className="px-2 py-2 text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                              title="Remove"
                            >
                              −
                            </button>
                          )}
                          {idx === packageCount.length - 1 && (
                            <button
                              type="button"
                              onClick={() => setPackageCount([...packageCount, ''])}
                              className="px-2 py-2 text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                              title="Add"
                            >
                              +
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {showValidation && fieldErrors['packageCount'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['packageCount']}</p>
                  )}
                </div>
              </div>
            )}

            {/* Sea Shipping Fields */}
            {shippingMethod === 'sea' && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Port of Loading *
                  </label>
                  <input
                    ref={portOfLoadingRef}
                    type="text"
                    value={portOfLoading}
                    onChange={(e) => setPortOfLoading(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['portOfLoading'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfLoading']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Port of Discharge *
                  </label>
                  <input
                    ref={portOfDischargeRef}
                    type="text"
                    value={portOfDischarge}
                    onChange={(e) => setPortOfDischarge(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['portOfDischarge'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfDischarge']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    BL Number *
                  </label>
                  <input
                    ref={blNumberRef}
                    type="text"
                    value={blNumber}
                    onChange={(e) => setBlNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['blNumber'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['blNumber']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shipping Line *
                  </label>
                  <input
                    ref={shippingLineRef}
                    type="text"
                    value={shippingLine}
                    onChange={(e) => setShippingLine(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['shippingLine'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['shippingLine']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vessel/Voyage *
                  </label>
                  <input
                    ref={vesselVoyageRef}
                    type="text"
                    value={vesselVoyage}
                    onChange={(e) => setVesselVoyage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['vesselVoyage'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['vesselVoyage']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity of Container *
                  </label>
                  <input
                    ref={containerQuantityRef}
                    type="text"
                    value={containerQuantity}
                    onChange={(e) => setContainerQuantity(e.target.value)}
                    placeholder="e.g., 2 containers"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  {showValidation && fieldErrors['containerQuantity'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['containerQuantity']}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Container Number *
                  </label>
                  <div className="space-y-2">
                    {containerNumber.map((val, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          ref={(el) => (containerNumberRefs.current[idx] = el)}
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const next = [...containerNumber];
                            next[idx] = e.target.value;
                            setContainerNumber(next);
                          }}
                          placeholder={idx === 0 ? 'e.g., ABCD1234567' : 'Additional container number'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required={idx === 0}
                        />
                        <div className="flex items-center gap-1">
                          {containerNumber.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setContainerNumber(containerNumber.filter((_, i) => i !== idx))}
                              className="px-2 py-2 text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                              title="Remove"
                            >
                              −
                            </button>
                          )}
                          {idx === containerNumber.length - 1 && (
                            <button
                              type="button"
                              onClick={() => setContainerNumber([...containerNumber, ''])}
                              className="px-2 py-2 text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                              title="Add"
                            >
                              +
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {showValidation && fieldErrors['containerNumber'] && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors['containerNumber']}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Invoice Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Date *
                </label>
                <input
                  ref={invoiceDateRef}
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {showValidation && fieldErrors['invoiceDate'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['invoiceDate']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  ref={currencyRef}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="AED">AED</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rate of Exchange
                </label>
                <input
                  ref={exchangeRateRef}
                  type="number"
                  value={exchangeRate}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    const nextValue = Number.isNaN(parsed) ? 0 : parsed;
                    setExchangeRate(roundTo(nextValue, 4));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            
            
            {/* Invoice Description */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Description *
              </label>
              <textarea
                value={invoiceDescription}
                onChange={(e) => setInvoiceDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter invoice description..."
                rows={3}
                required
              />
              {showValidation && !invoiceDescription.trim() && (
                <p className="mt-1 text-sm text-red-600">Invoice description is required</p>
              )}
            </div>
          </div>

          {/* Items with Cost/Sale Tabs */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-medium text-gray-900">Invoice Items</h2>
              <p className="text-sm text-gray-600 mt-1">Fill both Cost and Sale items before creating invoice</p>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-6">
              <button
                type="button"
                onClick={() => setActiveTab('cost')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'cost'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cost Items
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('sale')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'sale'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Sale Items
              </button>
            </div>

            {/* Cost Items Tab */}
            {activeTab === 'cost' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Cost Items:</strong> Internal tracking items that won't appear on the PDF or customer-facing documents.
                  </p>
                </div>
                {costItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Cost Item {index + 1}</h4>
                      {costItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCostItem(item.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Item Description *</label>
                        <AutocompleteInput
                          inputRef={{ current: costItemDescriptionRefs.current[index] }}
                          value={item.description}
                          onChange={(value) => updateCostItem(item.id, 'description', value)}
                          placeholder="Enter or select item description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_description`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_description`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                          ref={(el) => (costItemQuantityRefs.current[index] = el)}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateCostItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="1"
                          min="1"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_quantity`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_quantity`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                        <input
                          ref={(el) => (costItemRateRefs.current[index] = el)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => updateCostItem(item.id, 'rate', roundMoney(parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="0.00"
                          required
                        />
                        {showValidation && fieldErrors[`cost_item_${index}_rate`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`cost_item_${index}_rate`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                        <select
                          value={item.currency}
                          onChange={(e) => updateCostItem(item.id, 'currency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        >
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                          <option value="AED">AED</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900">
                            {item.currency} {roundMoney(item.amount).toFixed(2)}
                          </div>
                          {item.currency !== 'PKR' && (
                            <div className="text-xs text-blue-600">
                              PKR: {roundMoney(mulMoney(item.amount, exchangeRate)).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={addCostItem}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
                  >
                    + Add Cost Item
                  </button>
                </div>
              </div>
            )}

            {/* Sale Items Tab */}
            {activeTab === 'sale' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>Sale Items:</strong> These items will appear on the PDF and in ledgers. This is what the customer sees.
                  </p>
                </div>
                {saleItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Sale Item {index + 1}</h4>
                      {saleItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSaleItem(item.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Item Description *</label>
                        <AutocompleteInput
                          inputRef={{ current: saleItemDescriptionRefs.current[index] }}
                          value={item.description}
                          onChange={(value) => updateSaleItem(item.id, 'description', value)}
                          placeholder="Enter or select item description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_description`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_description`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                          ref={(el) => (saleItemQuantityRefs.current[index] = el)}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateSaleItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="1"
                          min="1"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_quantity`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_quantity`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                        <input
                          ref={(el) => (saleItemRateRefs.current[index] = el)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => updateSaleItem(item.id, 'rate', roundMoney(parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="0.00"
                          required
                        />
                        {showValidation && fieldErrors[`sale_item_${index}_rate`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors[`sale_item_${index}_rate`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                        <select
                          value={item.currency}
                          onChange={(e) => updateSaleItem(item.id, 'currency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        >
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                          <option value="AED">AED</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Total Sale:</span>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900">
                            {item.currency} {roundMoney(item.amount).toFixed(2)}
                          </div>
                          {item.currency !== 'PKR' && (
                            <div className="text-xs text-blue-600">
                              PKR: {roundMoney(mulMoney(item.amount, exchangeRate)).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={addSaleItem}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
                  >
                    + Add Sale Item
                  </button>
                </div>
              </div>
            )}

            {/* Deduction */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deduction (PKR)
                </label>
                <input
                  ref={discountRef}
                  type="number"
                  value={discount}
                  onChange={(e) => {
                    const next = parseFloat(e.target.value);
                    const parsed = isNaN(next) ? 0 : next;
                    setDiscount(Math.max(0, roundMoney(parsed)));
                  }}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Nature
                </label>
                <input
                  type="text"
                  value={discountNature}
                  onChange={(e) => setDiscountNature(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Early payment discount"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sale Subtotal (PKR):</span>
                  <span className="font-medium">PKR {roundMoney(calculateSubtotalPKR()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost Total (PKR):</span>
                  <span className="font-medium text-blue-600">PKR {roundMoney(calculateCostSubtotalPKR()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-600 border-t pt-2">
                  <span>Gross Profit (PKR):</span>
                  <span>PKR {roundMoney(calculateGrossProfit()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Deduction:</span>
                  <span className="font-medium">PKR {roundMoney(calculateDiscountPKR()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-600 border-t pt-2">
                  <span>Net Profit (PKR):</span>
                  <span>PKR {roundMoney(calculateNetProfit()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Final Total (PKR):</span>
                  <span>PKR {roundMoney(calculateTotalPKR()).toFixed(2)}</span>
                </div>
                {discount > 0 && calculateDiscountPKR() > calculateSubtotalPKR() && (
                  <div className="text-yellow-600 text-sm font-medium mt-2">
                    ⚠️ Warning: Discount exceeds subtotal!
                  </div>
                )}
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
                <p className="text-sm text-gray-600">
                  This invoice will show sale items converted to PKR using the exchange rate of {exchangeRate}.
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Cost items are for internal tracking only and won't appear on the PDF.
                </p>
                <p className="text-sm text-green-600 mt-1 font-medium">
                  Gross Profit margin: {calculateSubtotalPKR() > 0 ? ((calculateGrossProfit() / calculateSubtotalPKR()) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-sm text-green-600 mt-1 font-medium">
                  Net Profit margin: {calculateSubtotalPKR() > 0 ? ((calculateNetProfit() / calculateSubtotalPKR()) * 100).toFixed(1) : 0}%
                </p>
                {currency !== 'PKR' && (
                  <p className="text-sm text-blue-600 mt-2">
                    Original currency: {currency} {roundMoney(calculateTotal()).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </div>


          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handlePreviewPDF}
                disabled={isGeneratingPDF}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
              >
                {isGeneratingPDF ? 'Generating...' : 'Preview PDF'}
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft}
                className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50"
              >
                {isSavingDraft ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating Invoice...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Success Dialog */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          navigate('/all-invoices');
        }}
        title="Invoice Created Successfully!"
        message="Your invoice has been created and is now pending approval. You will be redirected to all revenue invoices."
        buttonText="Go to All Invoices"
      />
    </div>
  );
};

export default CreateInvoice;
